import { useEffect, useState } from 'react';
import { sportsVodApi } from '@/services/sportsVodApi';
import type { FfrHost } from '@/types/ffr';

export type OkruSourceStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseOkruSourceResult {
  status: OkruSourceStatus;
  /** API_BASE-relative signed proxy URL, or null. */
  url: string | null;
}

function initialFor(host: FfrHost, key: string): { key: string; status: OkruSourceStatus; url: string | null } {
  return { key, status: host === 'okru' ? 'loading' : 'idle', url: null };
}

/**
 * Resolves the native (ad-free) ok.ru proxy MP4 URL for the active part. For
 * any non-ok.ru host it stays 'idle' and never hits the network. 'error' means
 * the caller should fall back to the ok.ru iframe.
 *
 * The state is reset **synchronously during render** (React's "adjust state on
 * prop change" pattern) whenever the `host:part` key changes — NOT only in the
 * effect. If we reset in the effect alone, the first render after a part change
 * (e.g. native `ended` → advance) would still expose the PREVIOUS part's
 * resolved url under the new part's index, briefly mounting a `<video autoPlay>`
 * pointed at the just-finished, single-use signed proxy URL before self-
 * correcting. Resetting in render closes that window entirely.
 */
export function useOkruSource(titleId: string, host: FfrHost, part: number): UseOkruSourceResult {
  const key = `${host}:${part}`;
  const [state, setState] = useState(() => initialFor(host, key));
  if (state.key !== key) {
    setState(initialFor(host, key));
  }

  useEffect(() => {
    if (host !== 'okru') return;
    const controller = new AbortController();
    sportsVodApi
      .getFfrOkruSource(titleId, part, controller.signal)
      .then((resolved) => {
        if (controller.signal.aborted) return;
        setState((s) => (s.key !== key ? s : resolved ? { key, status: 'ready', url: resolved } : { key, status: 'error', url: null }));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err as Error)?.name === 'AbortError') return;
        setState((s) => (s.key !== key ? s : { key, status: 'error', url: null }));
      });
    return (): void => controller.abort();
  }, [titleId, host, part, key]);

  return { status: state.status, url: state.url };
}
